import app from 'flarum/forum/app';
import Page from 'flarum/common/components/Page';
import LoadingIndicator from 'flarum/common/components/LoadingIndicator';
import Placeholder from 'flarum/common/components/Placeholder';
import Button from 'flarum/common/components/Button';
import StatsLineChart from './StatsLineChart';
import StatsBarChart from './StatsBarChart';
const MONTH_NAMES_FR = ['janv.', 'févr.', 'mars', 'avr.', 'mai', 'juin', 'juil.', 'août', 'sept.', 'oct.', 'nov.', 'déc.'];
function formatPeriodLabel(period) {
  // Year granularity gives back a bare 'YYYY', month gives 'YYYY-MM'.
  if (period.length === 4) return period;
  const [year, month] = period.split('-');
  return `${MONTH_NAMES_FR[parseInt(month, 10) - 1]} ${year}`;
}
// Which of StatsResource's `*ByMonth` fields backs each toggle option, and
// the line color it gets - a distinct, stable color per metric rather than
// always reusing the theme's primary color, so switching between them
// reads as "a different chart" rather than "the same chart, don't look too
// closely at the numbers".
const SERIES = {
  posts: {
    dataKey: 'postsByMonth',
    labelKey: 'series_posts',
    color: '#2563eb'
  },
  discussions: {
    dataKey: 'discussionsByMonth',
    labelKey: 'series_discussions',
    color: '#16a34a'
  },
  users: {
    dataKey: 'usersByMonth',
    labelKey: 'series_users',
    color: '#9333ea'
  },
  species: {
    dataKey: 'speciesByMonth',
    labelKey: 'series_species',
    color: '#0d9488'
  }
};

// Colors chosen to loosely evoke each group (earthy brown for fungi, green
// for plants, red for insects, yellow for myxomycetes) rather than an
// arbitrary categorical palette - a small, purely decorative touch, but an
// easy one given there are only 5 groups to pick colors for.
const GROUPS = [{
  key: 'fungi',
  labelKey: 'group_fungi',
  color: '#b45309'
}, {
  key: 'plants',
  labelKey: 'group_plants',
  color: '#16a34a'
}, {
  key: 'insects',
  labelKey: 'group_insects',
  color: '#dc2626'
}, {
  key: 'myxo',
  labelKey: 'group_myxo',
  color: '#ca8a04'
}, {
  key: 'unclassified',
  labelKey: 'group_unclassified',
  color: '#6b7280'
}];

// StatsService's own RANK_KEYS, in the same taxonomic-hierarchy order (see
// its docblock) - drives the rank-by-kingdom table's own row order.
const RANKS = [{
  key: 'kingdom',
  labelKey: 'rank_kingdom'
}, {
  key: 'phylum',
  labelKey: 'rank_phylum'
}, {
  key: 'class',
  labelKey: 'rank_class'
}, {
  key: 'order',
  labelKey: 'rank_order'
}, {
  key: 'family',
  labelKey: 'rank_family'
}, {
  key: 'genus',
  labelKey: 'rank_genus'
}, {
  key: 'species',
  labelKey: 'rank_species'
}, {
  key: 'subspecies',
  labelKey: 'rank_subspecies'
}, {
  key: 'variety',
  labelKey: 'rank_variety'
}, {
  key: 'form',
  labelKey: 'rank_form'
}, {
  key: 'other',
  labelKey: 'rank_other'
}];

// StatsService's own KINGDOM_MAP values, plus 'unknown' - which kingdom
// column to show, in the order rankByKingdomSection() falls back to before
// sorting by each kingdom's own total (see there): rare enough on this
// forum (bacteria/archaea/viruses) that a fixed order for them barely
// matters in practice, 'unknown' is the one always pinned last regardless
// of count (a leftover/"don't know" bucket, not a real kingdom to rank by
// size).
const KINGDOMS = [{
  key: 'fungi',
  labelKey: 'kingdom_fungi'
}, {
  key: 'plantae',
  labelKey: 'kingdom_plantae'
}, {
  key: 'animalia',
  labelKey: 'kingdom_animalia'
}, {
  key: 'protozoa',
  labelKey: 'kingdom_protozoa'
}, {
  key: 'chromista',
  labelKey: 'kingdom_chromista'
}, {
  key: 'bacteria',
  labelKey: 'kingdom_bacteria'
}, {
  key: 'archaea',
  labelKey: 'kingdom_archaea'
}, {
  key: 'viruses',
  labelKey: 'kingdom_viruses'
}, {
  key: 'unknown',
  labelKey: 'kingdom_unknown'
}];

/**
 * /stats - site-wide statistics (StatsService/StatsResource): headline
 * totals, an interactive evolution chart (posts/discussions/members/
 * species, monthly or yearly, raw-per-period or cumulative), and a
 * species-by-taxon-group breakdown. Loads the whole (server-cached,
 * see StatsService) payload once up front - every transform below
 * (yearly aggregation, cumulative sums, chart point mapping) runs
 * client-side over that same in-memory data as the toggles change, no
 * extra requests.
 */
export default class StatsPage extends Page {
  stats = null;
  error = false;
  series = 'posts';
  granularity = 'month';
  mode = 'period';
  oninit(vnode) {
    super.oninit(vnode);
    app.setTitle(app.translator.trans('champis-others.forum.stats_page.title'));
    this.stats = null;
    this.error = false;
    this.series = 'posts';
    this.granularity = 'month';
    this.mode = 'period';
    this.load();
  }
  load() {
    app.request({
      method: 'GET',
      url: app.forum.attribute('apiUrl') + '/champis-stats'
    }).then(response => {
      const item = response.data && response.data[0];
      this.stats = item ? item.attributes : null;
      if (!item) this.error = true;
      m.redraw();
    }).catch(() => {
      this.error = true;
      m.redraw();
    });
  }
  view() {
    return m("div", {
      className: "Page StatsPage"
    }, m("div", {
      className: "container"
    }, m("h1", {
      className: "ChampisDiscussionList-header"
    }, m("i", {
      className: "icon fas fa-chart-line",
      "aria-hidden": "true"
    }), " ", app.translator.trans('champis-others.forum.stats_page.title')), !this.stats && this.error ? m(Placeholder, {
      text: app.translator.trans('champis-others.forum.stats_page.error')
    }) : !this.stats ? m(LoadingIndicator, null) : [this.headlineCards(), this.evolutionSection(), this.groupSection(), this.rankByKingdomSection()]));
  }
  headlineCards() {
    const s = this.stats;
    const t = function (key, params) {
      if (params === void 0) {
        params = {};
      }
      return app.translator.trans(`champis-others.forum.stats_page.${key}`, params);
    };
    const format = v => new Intl.NumberFormat('fr-FR').format(v);
    const busiest = this.busiestMonth(s.postsByMonth);
    return m("div", {
      className: "StatsPage-cards"
    }, m("div", {
      className: "StatsPage-card"
    }, m("span", {
      className: "StatsPage-cardValue"
    }, format(this.total(s.postsByMonth))), m("span", {
      className: "StatsPage-cardLabel"
    }, t('total_posts_label'))), m("div", {
      className: "StatsPage-card"
    }, m("span", {
      className: "StatsPage-cardValue"
    }, format(this.total(s.discussionsByMonth))), m("span", {
      className: "StatsPage-cardLabel"
    }, t('total_discussions_label'))), m("div", {
      className: "StatsPage-card"
    }, m("span", {
      className: "StatsPage-cardValue"
    }, format(this.total(s.usersByMonth))), m("span", {
      className: "StatsPage-cardLabel"
    }, t('total_users_label'))), m("div", {
      className: "StatsPage-card"
    }, m("span", {
      className: "StatsPage-cardValue"
    }, format(s.speciesTotalWithoutSynonyms)), m("span", {
      className: "StatsPage-cardLabel"
    }, t('total_species_label')), m("span", {
      className: "StatsPage-cardHint"
    }, t('species_with_synonyms_hint', {
      count: format(s.speciesTotalWithSynonyms)
    }))), busiest && m("div", {
      className: "StatsPage-card"
    }, m("span", {
      className: "StatsPage-cardValue"
    }, formatPeriodLabel(busiest.month)), m("span", {
      className: "StatsPage-cardLabel"
    }, t('busiest_month_label')), m("span", {
      className: "StatsPage-cardHint"
    }, t('busiest_month_hint', {
      count: format(busiest.count)
    }))));
  }
  evolutionSection() {
    const t = key => app.translator.trans(`champis-others.forum.stats_page.${key}`);
    return m("div", {
      className: "StatsPage-section"
    }, m("h2", {
      className: "StatsPage-sectionTitle"
    }, t('evolution_title')), m("div", {
      className: "StatsPage-controls"
    }, m("div", {
      className: "StatsPage-toggle"
    }, Object.keys(SERIES).map(key => m(Button, {
      key: key,
      className: "Button",
      active: this.series === key,
      onclick: () => this.series = key
    }, t(SERIES[key].labelKey)))), m("div", {
      className: "StatsPage-toggle"
    }, m(Button, {
      className: "Button",
      active: this.granularity === 'month',
      onclick: () => this.granularity = 'month'
    }, t('granularity_month')), m(Button, {
      className: "Button",
      active: this.granularity === 'year',
      onclick: () => this.granularity = 'year'
    }, t('granularity_year'))), m("div", {
      className: "StatsPage-toggle"
    }, m(Button, {
      className: "Button",
      active: this.mode === 'period',
      onclick: () => this.mode = 'period'
    }, t('mode_period')), m(Button, {
      className: "Button",
      active: this.mode === 'cumulative',
      onclick: () => this.mode = 'cumulative'
    }, t('mode_cumulative')))), m(StatsLineChart, {
      points: this.chartPoints(),
      color: SERIES[this.series].color
    }));
  }
  groupSection() {
    const t = key => app.translator.trans(`champis-others.forum.stats_page.${key}`);
    const counts = this.stats.speciesByGroup || {};
    const bars = GROUPS.map(group => ({
      key: group.key,
      label: t(group.labelKey),
      value: counts[group.key] || 0,
      color: group.color
    }));
    return m("div", {
      className: "StatsPage-section"
    }, m("h2", {
      className: "StatsPage-sectionTitle"
    }, t('species_by_group_title')), m("p", {
      className: "StatsPage-sectionHint"
    }, t('species_by_group_hint')), m(StatsBarChart, {
      bars: bars
    }));
  }

  // Rank x kingdom cross-tab: kingdom columns (present ones only, largest
  // first, 'unknown' pinned last regardless of its own count - see KINGDOMS'
  // own comment), rank rows in taxonomic order, a running total on both
  // axes so a viewer can sanity-check either direction against the
  // headline "Taxons" card without doing the arithmetic themselves.
  rankByKingdomSection() {
    const t = key => app.translator.trans(`champis-others.forum.stats_page.${key}`);
    const format = v => new Intl.NumberFormat('fr-FR').format(v);
    const byKingdom = this.stats.speciesByRankAndKingdom || {};
    const cell = (kingdomKey, rankKey) => (byKingdom[kingdomKey] || {})[rankKey] || 0;
    const kingdomTotal = kingdomKey => Object.values(byKingdom[kingdomKey] || {}).reduce((sum, v) => sum + v, 0);
    const present = KINGDOMS.filter(k => kingdomTotal(k.key) > 0);
    const known = present.filter(k => k.key !== 'unknown').sort((a, b) => kingdomTotal(b.key) - kingdomTotal(a.key));
    const unknown = present.find(k => k.key === 'unknown');
    const columns = unknown ? [...known, unknown] : known;
    const rankTotal = rankKey => columns.reduce((sum, k) => sum + cell(k.key, rankKey), 0);
    const grandTotal = columns.reduce((sum, k) => sum + kingdomTotal(k.key), 0);
    return m("div", {
      className: "StatsPage-section"
    }, m("h2", {
      className: "StatsPage-sectionTitle"
    }, t('species_by_rank_title')), m("p", {
      className: "StatsPage-sectionHint"
    }, t('species_by_rank_hint')), m("div", {
      className: "StatsPage-tableWrap"
    }, m("table", {
      className: "StatsPage-table"
    }, m("thead", null, m("tr", null, m("th", null), columns.map(k => m("th", {
      key: k.key
    }, t(k.labelKey))), m("th", null, t('table_total_label')))), m("tbody", null, RANKS.map(rank => m("tr", {
      key: rank.key
    }, m("th", null, t(rank.labelKey)), columns.map(k => m("td", {
      key: k.key
    }, format(cell(k.key, rank.key)))), m("td", {
      className: "StatsPage-tableTotal"
    }, format(rankTotal(rank.key)))))), m("tfoot", null, m("tr", null, m("th", null, t('table_total_label')), columns.map(k => m("td", {
      key: k.key,
      className: "StatsPage-tableTotal"
    }, format(kingdomTotal(k.key)))), m("td", {
      className: "StatsPage-tableTotal"
    }, format(grandTotal)))))));
  }

  // {month: 'YYYY-MM', count}[] -> {label, value}[] chart points, per the
  // current series/granularity/mode toggles.
  chartPoints() {
    const raw = this.stats[SERIES[this.series].dataKey] || [];
    let periods = raw.map(r => ({
      period: r.month,
      value: r.count
    }));
    if (this.granularity === 'year') {
      periods = this.aggregateByYear(periods);
    }
    if (this.mode === 'cumulative') {
      periods = this.cumulative(periods);
    }
    return periods.map(p => ({
      label: formatPeriodLabel(p.period),
      value: p.value
    }));
  }
  aggregateByYear(periods) {
    const totalsByYear = new Map();
    periods.forEach(p => {
      const year = p.period.slice(0, 4);
      totalsByYear.set(year, (totalsByYear.get(year) || 0) + p.value);
    });
    return Array.from(totalsByYear.entries()).map(_ref => {
      let [year, value] = _ref;
      return {
        period: year,
        value
      };
    });
  }
  cumulative(periods) {
    let running = 0;
    return periods.map(p => {
      running += p.value;
      return {
        period: p.period,
        value: running
      };
    });
  }
  total(monthly) {
    return (monthly || []).reduce((sum, r) => sum + r.count, 0);
  }
  busiestMonth(monthly) {
    if (!monthly || monthly.length === 0) return null;
    return monthly.reduce((best, r) => r.count > best.count ? r : best, monthly[0]);
  }
}
flarum.reg.add('champis-others', 'forum/components/StatsPage', StatsPage);